/**
 * Utility functions for error handling and validation
 */

/**
 * Normalizes an error (string or object) to a displayable string for dialogs/toasts.
 */
export const getErrorMessage = (error: string | object | null | undefined): string => {
  if (error == null) {
    return 'An unexpected error occurred'
  }
  if (typeof error === 'string') {
    return error
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }

  return String(error)
}

/**
 * Checks if an error payload represents an unauthorized (403) or forbidden (401) error.
 * This function handles various error formats that may come from different API clients or error handlers.
 *
 * @param errorPayload - The error object or string to check. Can be:
 *   - An error object with statusCode, status, error, response, or message properties
 *   - A string error message
 *   - null or undefined
 *
 * @returns true if the error is a 403 Forbidden or 401 Unauthorized error, false otherwise
 *
 * @example
 * ```typescript
 * const error = { statusCode: 403, message: 'Access denied' }
 * if (isUnauthorizedError(error)) {
 *   router.replace(ROUTES.UNAUTHORIZED)
 * }
 * ```
 */
export const isUnauthorizedError = (errorPayload: any): boolean => {
  if (!errorPayload) {
    return false
  }

  // Handle string errors - check if message contains unauthorized/forbidden keywords
  if (typeof errorPayload === 'string') {
    const lowerMessage = errorPayload.toLowerCase()

    return (
      lowerMessage.includes('forbidden') ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('does not have access')
    )
  }

  // Handle object errors
  if (typeof errorPayload === 'object' && errorPayload !== null) {
    // Check statusCode (common in API error responses)
    if (errorPayload.statusCode === 403 || errorPayload.statusCode === 401) {
      return true
    }

    // Check error property (common in HTTP error objects)
    if (errorPayload.error === 'Forbidden' || errorPayload.error === 'Unauthorized') {
      return true
    }

    // Check response.status (common in axios errors)
    if (errorPayload.response?.status === 403 || errorPayload.response?.status === 401) {
      return true
    }

    // Check status property (alternative error format)
    if (errorPayload.status === 403 || errorPayload.status === 401) {
      return true
    }

    // Check message content for unauthorized/forbidden keywords
    if (errorPayload.message) {
      const lowerMessage = String(errorPayload.message).toLowerCase()
      if (
        lowerMessage.includes('forbidden') ||
        lowerMessage.includes('unauthorized') ||
        lowerMessage.includes('does not have access')
      ) {
        return true
      }
    }
  }

  return false
}
